import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_2fa6389e-f1d2-497c-bca2-03a57f9bf7e3/artifacts/tt6eqk8f_Gemini_Generated_Image_94rohl94rohl94ro.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  show: { transition: { staggerChildren: 0.12 } },
};

const trust = ["20+ Years Experience", "5,000+ Careers Built", "99% Visa Success Rate"];

const CALENDLY_URL = "https://cal.com/mindways/discovery-call";

export default function Hero({ onBookCall }) {
  const scrollToProcess = (e) => {
    e.preventDefault();
    const el = document.querySelector("#process");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      style={{
        paddingTop: "100px",
        paddingBottom: "0",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Background mesh */}
      <div style={{
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        background: "radial-gradient(ellipse 80% 60% at 60% 40%, rgba(43,47,158,0.05) 0%, rgba(255,255,255,0) 70%)",
        pointerEvents: "none",
      }} />

      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div
          style={{ gap: "60px", minHeight: "calc(100vh - 110px)", paddingTop: "40px", paddingBottom: "60px" }}
          className="mw-grid-2"
        >
          {/* Text content */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={stagger}
            style={{ display: "flex", flexDirection: "column", gap: "28px" }}
          >
            {/* Label */}
            <motion.div variants={fadeUp}>
              <span style={{
                fontSize: "12px",
                fontWeight: "700",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#F5A000",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                Founder-led Overseas Education Counselling · Est. 2005
              </span>
            </motion.div>

            {/* H1 */}
            <motion.h1
              variants={fadeUp}
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(36px, 5vw, 64px)",
                fontWeight: "700",
                lineHeight: "1.1",
                color: "#0F172A",
                margin: 0,
              }}
            >
              Overseas admissions,{" "}
              <span style={{ color: "#2B2F9E" }}>simplified.</span>
              <br />
              Strategy first.{" "}
              <span style={{ fontStyle: "italic", color: "#F5A000" }}>Execution done right.</span>
            </motion.h1>

            {/* Subhead */}
            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "18px",
                lineHeight: "1.7",
                color: "#64748B",
                fontFamily: "'DM Sans', sans-serif",
                margin: 0,
                maxWidth: "540px",
              }}
            >
              Career clarity, university shortlisting, applications, tests, scholarships, visa, and pre-departure.
              All guided by a counselling-led process built since 2005.
            </motion.p>

            {/* Trust line */}
            <motion.div variants={fadeUp} style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
              {trust.map((t) => (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <CheckCircle size={16} style={{ color: "#F5A000", flexShrink: 0 }} />
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#0F172A", fontFamily: "'DM Sans', sans-serif" }}>{t}</span>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div variants={fadeUp} style={{ display: "flex", flexWrap: "wrap", gap: "14px", alignItems: "center" }}>
              <button
                data-testid="hero-book-call-btn"
                onClick={() => window.open(CALENDLY_URL, "_blank")}
                style={{
                  backgroundColor: "#2B2F9E",
                  color: "#fff",
                  border: "none",
                  padding: "16px 32px",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "700",
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: "0 8px 24px rgba(43,47,158,0.3)",
                  transition: "all 0.25s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#1e227a"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#2B2F9E"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                Book a Strategy Call <ArrowRight size={16} />
              </button>
              <a
                href="#process"
                data-testid="hero-see-process-link"
                onClick={scrollToProcess}
                style={{
                  color: "#2B2F9E",
                  textDecoration: "none",
                  fontSize: "15px",
                  fontWeight: "600",
                  fontFamily: "'DM Sans', sans-serif",
                  borderBottom: "2px solid transparent",
                  paddingBottom: "2px",
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={(e) => { e.target.style.borderColor = "#2B2F9E"; }}
                onMouseLeave={(e) => { e.target.style.borderColor = "transparent"; }}
              >
                See how the process works
              </a>
            </motion.div>

            {/* Microcopy */}
            <motion.p
              variants={fadeUp}
              style={{ fontSize: "13px", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif", margin: 0 }}
            >
              For UG, Masters, MBA, and specialised pathways like Law and Medical education.
            </motion.p>
          </motion.div>

          {/* Hero image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
            style={{ position: "relative" }}
            className="hidden md:block"
          >
            {/* Process flow badge */}
            <div style={{
              position: "absolute",
              top: "24px",
              left: "-20px",
              zIndex: 10,
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "12px 18px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              border: "1px solid #E2E8F0",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: "600", color: "#2B2F9E", fontFamily: "'DM Sans', sans-serif" }}>
                {["Clarity", "Shortlist", "Apply", "Visa", "Depart"].map((step, i) => (
                  <React.Fragment key={step}>
                    <span>{step}</span>
                    {i < 4 && <span style={{ color: "#F5A000" }}>›</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Stats badge */}
            <div style={{
              position: "absolute",
              bottom: "32px",
              left: "-20px",
              zIndex: 10,
              backgroundColor: "#2B2F9E",
              color: "#fff",
              borderRadius: "10px",
              padding: "16px 20px",
              boxShadow: "0 8px 32px rgba(43,47,158,0.3)",
            }}>
              <div style={{ fontSize: "28px", fontWeight: "700", fontFamily: "'Playfair Display', serif" }}>100K+</div>
              <div style={{ fontSize: "12px", fontFamily: "'DM Sans', sans-serif", opacity: 0.85 }}>Counselling Hours</div>
            </div>

            <div style={{
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.12)",
              aspectRatio: "4/5",
            }}>
              <img
                src={HERO_IMAGE}
                alt="Students planning overseas education"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div style={{
        position: "absolute",
        bottom: 0, left: 0, right: 0,
        height: "80px",
        background: "linear-gradient(to bottom, rgba(255,255,255,0), #f8fafc)",
        pointerEvents: "none",
      }} />
    </section>
  );
}
